import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FooterSection = () => {
  const currentYear = new Date()?.getFullYear();

  const footerLinks = {
    product: [
      { name: 'Features', href: '#features' },
      { name: 'Pricing', href: '#pricing' },
      { name: 'FAQ', href: '#faq' },
      { name: 'Roadmap', href: '#' }
    ],
    company: [
      { name: 'About Us', href: '#' },
      { name: 'Blog', href: '#' },
      { name: 'Careers', href: '#' },
      { name: 'Press Kit', href: '#' }
    ],
    support: [
      { name: 'Help Center', href: '#' },
      { name: 'Contact Us', href: '/contact' },
      { name: 'Community', href: '#' },
      { name: 'Status', href: '#' }
    ],
    legal: [
      { name: 'Privacy Policy', href: '/privacy-policy' },
      { name: 'Terms of Service', href: '/terms-of-service' },
      { name: 'Cookie Policy', href: '#' },
      { name: 'Accessibility', href: '#' }
    ]
  };

  const socialLinks = [
    { name: 'Twitter', icon: 'Twitter', href: '#' },
    { name: 'Facebook', icon: 'Facebook', href: '#' },
    { name: 'Instagram', icon: 'Instagram', href: '#' },
    { name: 'LinkedIn', icon: 'Linkedin', href: '#' },
    { name: 'YouTube', icon: 'Youtube', href: '#' }
  ];

  return (
    <footer className="bg-primary text-primary-foreground">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-6 gap-8">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <Link to="/homepage" className="flex items-center space-x-3 mb-6">
              <div className="relative">
                <img 
                  src="/assets/images/IMG_3512-1760460463517.jpg"
                  alt="Cynizee diamond logo with gradient background"
                  className="w-12 h-12 rounded-lg object-cover breathing"
                />
              </div>
              <div className="flex flex-col">
                <span className="text-2xl font-heading font-bold text-white">
                  Cynizee
                </span>
                <span className="text-sm text-primary-foreground/80 font-medium -mt-1">
                  Marketing Hub
                </span>
              </div>
            </Link>
            
            <p className="text-primary-foreground/80 leading-relaxed mb-6 max-w-sm">
              Transforming family chaos into harmony through smart organization tools 
              that kids love and parents control. Built for real families, designed for lasting change.
            </p>

            {/* Newsletter Signup */}
            <div className="space-y-4">
              <h4 className="font-heading font-semibold text-white">Stay Updated</h4>
              <div className="flex space-x-2">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-secondary"
                />
                <Button
                  variant="default"
                  className="bg-secondary hover:bg-secondary/90 text-secondary-foreground"
                  iconName="Send"
                  iconSize={16}
                >
                  Subscribe
                </Button>
              </div>
              <p className="text-xs text-primary-foreground/60">
                Get family organization tips and product updates. No spam, ever.
              </p>
            </div>
          </div>

          {/* Links Sections */}
          <div className="lg:col-span-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Product */}
            <div>
              <h4 className="font-heading font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-3">
                {footerLinks?.product?.map((link) => (
                  <li key={link?.name}>
                    <a
                      href={link?.href}
                      className="text-primary-foreground/80 hover:text-white transition-colors duration-200 text-sm"
                    >
                      {link?.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="font-heading font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-3">
                {footerLinks?.company?.map((link) => (
                  <li key={link?.name}>
                    <a
                      href={link?.href}
                      className="text-primary-foreground/80 hover:text-white transition-colors duration-200 text-sm"
                    >
                      {link?.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="font-heading font-semibold text-white mb-4">Support</h4>
              <ul className="space-y-3">
                {footerLinks?.support?.map((link) => (
                  <li key={link?.name}>
                    <Link
                      to={link?.href}
                      className="text-primary-foreground/80 hover:text-white transition-colors duration-200 text-sm"
                    >
                      {link?.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="font-heading font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-3">
                {footerLinks?.legal?.map((link) => (
                  <li key={link?.name}>
                    <Link
                      to={link?.href}
                      className="text-primary-foreground/80 hover:text-white transition-colors duration-200 text-sm"
                    >
                      {link?.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Social Links & Stats */}
        <div className="border-t border-white/10 mt-12 pt-8">
          <div className="flex flex-col lg:flex-row justify-between items-center space-y-6 lg:space-y-0">
            {/* Social Links */}
            <div className="flex items-center space-x-6">
              <span className="text-primary-foreground/80 text-sm font-medium">Follow us:</span>
              <div className="flex space-x-4">
                {socialLinks?.map((social) => (
                  <a
                    key={social?.name}
                    href={social?.href}
                    className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-white/20 transition-colors duration-200"
                    aria-label={social?.name}
                  >
                    <Icon name={social?.icon} size={18} className="text-white" />
                  </a>
                ))}
              </div>
            </div>

            {/* Stats */}
            <div className="flex items-center space-x-8">
              <div className="text-center">
                <div className="text-2xl font-heading font-bold text-white">2,500+</div>
                <div className="text-xs text-primary-foreground/60">Families Waiting</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-heading font-bold text-white">Q2 2024</div>
                <div className="text-xs text-primary-foreground/60">Launch Target</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-heading font-bold text-white">95%</div>
                <div className="text-xs text-primary-foreground/60">Satisfaction Rate</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-6">
              <p className="text-primary-foreground/60 text-sm">
                © {currentYear} Cynizee. All rights reserved.
              </p>
              
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-success rounded-full"></div>
                  <span className="text-primary-foreground/60 text-xs">All systems operational</span>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <Icon name="Shield" size={16} className="text-success" />
                <span className="text-primary-foreground/60 text-xs">Privacy Protected</span>
              </div>
              
              <div className="flex items-center space-x-2">
                <Icon name="Heart" size={16} className="text-accent" />
                <span className="text-primary-foreground/60 text-xs">Made with love for families</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default FooterSection;